/**
 * Hub for location plan and actual Excel loads, plus who changed what.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import api from '@/services/api';
import { KpiLoadWidget } from './loads/KpiLoadWidget';
import { KpiLoadHistory } from './loads/KpiLoadHistory';
import type { LoadHistoryChange, LoadHistoryUpload } from './loads/types';

export const KpiLoadsPage: React.FC = () => {
  const { t } = useLanguage();
  const [uploads, setUploads] = useState<LoadHistoryUpload[]>([]);
  const [changes, setChanges] = useState<LoadHistoryChange[]>([]);
  const [loading, setLoading] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/business-performance/loads/history');
      const data = response.data?.data || {};
      setUploads(data.uploads || []);
      setChanges(data.changes || []);
    } catch {
      setUploads([]);
      setChanges([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadHistory();
  }, [loadHistory]);

  return (
    <div className="min-h-screen bg-[var(--color-secondary-bg)]">
      <div className="container mx-auto px-2 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <div>
            <h1 className="text-xl font-bold text-[var(--color-surface-text)] mb-0.5">
              {t('kpi.loadTitle')}
            </h1>
            <p className="text-xs text-[var(--color-secondary-text)]">{t('kpi.loadHubHint')}</p>
          </div>
          <Link
            to="/kpis"
            className="text-xs text-[var(--color-primary-text)] hover:underline"
          >
            {t('kpi.loadBackCatalog')}
          </Link>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-3">
          <KpiLoadWidget kind="target" t={t} onCommitted={loadHistory} />
          <KpiLoadWidget kind="actual" t={t} onCommitted={loadHistory} />
        </div>
        <KpiLoadHistory uploads={uploads} changes={changes} loading={loading} t={t} />
      </div>
    </div>
  );
};
